import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Desktop } from "./Desktop";
import type { DesktopApp, DesktopConfig } from "../scenario/types";
import { tx, txr } from "../lib/tx";

/**
 * MalwareDesk: "the game that wanted more".
 *
 * A Windows desktop with a light Chrome window. The learner opens a news page, follows an ad to a
 * friendly game site, answers a notification prompt, plays a short puzzle round, is offered an
 * extension, then meets a fake "compatibility problem" that asks for a download and a command.
 * Every risky step is stopped by a company control. Nothing here installs, runs or reaches out.
 *
 * Everything is drawn in DOM/SVG at a fixed design size (572px tall above the taskbar) and scaled to
 * the monitor, so the same layout fits any screen. All classes are prefixed mlw- (malware-desk.css).
 */

/** What the top-right guide card should say. The parent renders it; this file owns the words. */
export interface MalwareGuide {
  step: string;
  title: string;
  body: string;
  /** Exact on-screen button label. The parent renders it as a clickable word. */
  actionLabel?: string;
}

/* ------------------------------------------------------------------------------------------------
 * Flow types and state
 * ---------------------------------------------------------------------------------------------- */

type Stage = "desk" | "news" | "lobby" | "notify" | "play" | "offer" | "freeze" | "repair" | "left" | "debrief";
type TabId = "news" | "game";
type Notif = "unseen" | "pending" | "allowed" | "blocked" | "dismissed";
type Answer = "allowed" | "blocked" | "dismissed";
type OfferView = "offer" | "listing" | "blocked";
type FreezePhase = "board" | "glitch" | "frozen";
type RepairView = "download" | "exeBlocked" | "steps" | "cmdBlocked" | "reported";
type Leaving = "tab" | "window" | null;
type AdMode = "live" | "switch" | "used";
type Ghost = "repair" | "run" | "steps" | "runSteps";

const GAME_STAGES: readonly Stage[] = ["lobby", "notify", "play", "offer", "freeze", "repair"];
const inGame = (stage: Stage): boolean => GAME_STAGES.includes(stage);

const SITE: Record<TabId, { host: string; path: string; title: string }> = {
  news: { host: "harbor-daily.example", path: "/", title: "Harbor Daily" },
  game: { host: "mindspark.example", path: "/play", title: "MindSpark" },
};

/** The learner's journey, kept for the debrief. Nothing here leaves this component. */
interface Journey {
  notif: Notif;
  reachedOffer: boolean;
  extViewed: boolean;
  extTried: boolean;
  sawFreeze: boolean;
  sawRepair: boolean;
  ranExe: boolean;
  ranCmd: boolean;
  toldIT: boolean;
  /** Stage the learner was in when they first closed the game tab or the browser. */
  leftFrom: Stage | null;
  toastShown: number;
  toastBlocked: boolean;
}

interface BrowserState {
  open: boolean;
  news: boolean;
  game: boolean;
  active: TabId;
}

interface State {
  stage: Stage;
  browser: BrowserState;
  /** Which tab shows the loading indicator. */
  loading: TabId | null;
  /** The game tab shows a page skeleton while it first loads. */
  skeleton: boolean;
  /** Play was pressed; the site is about to ask for notifications. */
  starting: boolean;
  closing: Leaving;
  playDone: { correct: number; timedOut: boolean } | null;
  offerView: OfferView;
  freezePhase: FreezePhase;
  repairView: RepairView;
  toast: boolean;
  nudge: string | null;
  replay: boolean;
  j: Journey;
}

const INITIAL: State = {
  stage: "desk",
  browser: { open: false, news: false, game: false, active: "news" },
  loading: null,
  skeleton: false,
  starting: false,
  closing: null,
  playDone: null,
  offerView: "offer",
  freezePhase: "board",
  repairView: "download",
  toast: false,
  nudge: null,
  replay: false,
  j: {
    notif: "unseen",
    reachedOffer: false,
    extViewed: false,
    extTried: false,
    sawFreeze: false,
    sawRepair: false,
    ranExe: false,
    ranCmd: false,
    toldIT: false,
    leftFrom: null,
    toastShown: 0,
    toastBlocked: false,
  },
};

type Action =
  | { t: "openBrowser" }
  | { t: "closeWindow" }
  | { t: "closeTab"; tab: TabId }
  | { t: "closeDone" }
  | { t: "selectTab"; tab: TabId }
  | { t: "reload" }
  | { t: "loaded" }
  | { t: "openAd" }
  | { t: "press" }
  | { t: "ask" }
  | { t: "answer"; v: Answer }
  | { t: "playDone"; correct: number; timedOut: boolean }
  | { t: "toOffer" }
  | { t: "seeListing" }
  | { t: "listingBack" }
  | { t: "addExt" }
  | { t: "continueBasic" }
  | { t: "freezeTo"; phase: FreezePhase }
  | { t: "repair" }
  | { t: "run" }
  | { t: "showSteps" }
  | { t: "runSteps" }
  | { t: "tellIT" }
  | { t: "toastIn" }
  | { t: "resolve" }
  | { t: "blockSite" }
  | { t: "dismissToast" }
  | { t: "toDebrief" }
  | { t: "replay"; on: boolean }
  | { t: "nudge"; msg: string | null };

/** Record where the learner left the game (first time only) and settle a prompt that was still open. */
function markLeft(s: State): Journey {
  if (!inGame(s.stage)) return s.j;
  return {
    ...s.j,
    leftFrom: s.j.leftFrom ?? s.stage,
    notif: s.j.notif === "pending" ? "dismissed" : s.j.notif,
  };
}

function reduce(s: State, a: Action): State {
  switch (a.t) {
    case "openBrowser": {
      if (s.browser.open || s.stage === "debrief") return s;
      return {
        ...s,
        stage: s.stage === "desk" ? "news" : s.stage,
        loading: "news",
        skeleton: false,
        closing: null,
        browser: { open: true, news: true, game: false, active: "news" },
      };
    }
    case "closeWindow": {
      if (!s.browser.open || s.closing) return s;
      return { ...s, closing: "window", j: markLeft(s) };
    }
    case "closeTab": {
      if (s.closing || !s.browser.open) return s;
      if (a.tab === "news") {
        if (!s.browser.news) return s;
        if (s.browser.game) return { ...s, browser: { ...s.browser, news: false, active: "game" } };
        return { ...s, closing: "window", j: markLeft(s) };
      }
      if (!s.browser.game) return s;
      return { ...s, closing: "tab", j: markLeft(s) };
    }
    case "closeDone": {
      if (!s.closing) return s;
      const leftGame = inGame(s.stage);
      // The warning is shown once, when they first leave. Closing the tab
      // after the repair (or after that warning already opened the repair)
      // must not put the same toast back on the news page.
      const repairDone = s.stage === "repair" && (s.j.toldIT || s.j.toastShown > 0 || s.repairView === "reported");
      const nextStage: Stage = repairDone ? "debrief" : leftGame ? "left" : s.stage === "news" ? "desk" : s.stage;
      if (s.closing === "window") {
        return {
          ...s,
          closing: null,
          loading: null,
          skeleton: false,
          starting: false,
          stage: nextStage,
          browser: { open: false, news: false, game: false, active: "news" },
        };
      }
      return {
        ...s,
        closing: null,
        loading: null,
        skeleton: false,
        starting: false,
        stage: nextStage,
        browser: { open: s.browser.news, news: s.browser.news, game: false, active: "news" },
      };
    }
    case "selectTab": {
      if (!s.browser.open || s.closing) return s;
      if (a.tab === "news" ? !s.browser.news : !s.browser.game) return s;
      return { ...s, browser: { ...s.browser, active: a.tab } };
    }
    case "reload":
      return s.browser.open && !s.closing ? { ...s, loading: s.browser.active } : s;
    case "loaded":
      return { ...s, loading: null, skeleton: false };
    case "openAd": {
      if (s.browser.game) return { ...s, browser: { ...s.browser, active: "game" } };
      if (s.stage !== "news") return s;
      return {
        ...s,
        stage: "lobby",
        loading: "game",
        skeleton: true,
        starting: false,
        browser: { ...s.browser, game: true, active: "game" },
      };
    }
    case "press":
      return s.stage === "lobby" && !s.starting && !s.skeleton ? { ...s, starting: true } : s;
    case "ask":
      return s.stage === "lobby" && s.starting
        ? { ...s, starting: false, stage: "notify", j: { ...s.j, notif: "pending" } }
        : s;
    case "answer":
      return s.stage === "notify" ? { ...s, stage: "play", playDone: null, j: { ...s.j, notif: a.v } } : s;
    case "playDone":
      return s.stage === "play" && !s.playDone ? { ...s, playDone: { correct: a.correct, timedOut: a.timedOut } } : s;
    case "toOffer":
      return s.stage === "play" ? { ...s, stage: "offer", offerView: "offer", j: { ...s.j, reachedOffer: true } } : s;
    case "seeListing":
      return s.stage === "offer" ? { ...s, offerView: "listing", j: { ...s.j, extViewed: true } } : s;
    case "listingBack":
      return s.stage === "offer" ? { ...s, offerView: "offer" } : s;
    case "addExt":
      return s.stage === "offer"
        ? { ...s, offerView: "blocked", j: { ...s.j, extViewed: true, extTried: true } }
        : s;
    case "continueBasic":
      return s.stage === "offer" ? { ...s, stage: "freeze", freezePhase: "board" } : s;
    case "freezeTo": {
      if (s.stage !== "freeze") return s;
      return { ...s, freezePhase: a.phase, j: a.phase === "board" ? s.j : { ...s.j, sawFreeze: true } };
    }
    case "repair":
      return s.stage === "freeze" && s.freezePhase === "frozen"
        ? { ...s, stage: "repair", repairView: "download", j: { ...s.j, sawRepair: true } }
        : s;
    case "run":
      return s.stage === "repair" && s.repairView === "download"
        ? { ...s, repairView: "exeBlocked", j: { ...s.j, ranExe: true } }
        : s;
    case "showSteps":
      return s.stage === "repair" && s.repairView === "exeBlocked" ? { ...s, repairView: "steps" } : s;
    case "runSteps":
      return s.stage === "repair" && s.repairView === "steps"
        ? { ...s, repairView: "cmdBlocked", j: { ...s.j, ranCmd: true } }
        : s;
    case "tellIT":
      return s.stage === "repair" && s.repairView === "cmdBlocked"
        ? { ...s, repairView: "reported", j: { ...s.j, toldIT: true } }
        : s;
    case "toastIn":
      return s.stage === "left" ? { ...s, toast: true, j: { ...s.j, toastShown: s.j.toastShown + 1 } } : s;
    case "resolve":
      if (s.stage !== "left") return s;
      if (s.j.toldIT || s.j.sawRepair) {
        return { ...s, toast: false, stage: "debrief" };
      }
      return {
        ...s,
        toast: false,
        stage: "repair",
        repairView: "download",
        loading: "game",
        skeleton: true,
        closing: null,
        browser: { open: true, news: s.browser.open && s.browser.news, game: true, active: "game" },
        j: { ...s.j, sawRepair: true },
      };
    case "blockSite":
      return s.stage === "left" ? { ...s, toast: false, stage: "debrief", j: { ...s.j, toastBlocked: true } } : s;
    case "dismissToast":
      return s.stage === "left" ? { ...s, toast: false, stage: "debrief" } : s;
    case "toDebrief":
      return s.stage === "left" ? { ...s, toast: false, stage: "debrief" } : s;
    case "replay":
      return s.stage === "debrief" ? { ...s, replay: a.on } : s;
    case "nudge":
      return { ...s, nudge: a.msg };
    default:
      return s;
  }
}

/* ------------------------------------------------------------------------------------------------
 * Guide (the short coach in the top-right card)
 * ---------------------------------------------------------------------------------------------- */

const STEP: Record<Stage, string> = {
  desk: "1 \u00b7 Desktop",
  news: "2 \u00b7 Harbor Daily",
  lobby: "3 \u00b7 MindSpark",
  notify: "3 \u00b7 MindSpark",
  play: "3 \u00b7 MindSpark",
  offer: "4 \u00b7 Upgrade",
  freeze: "5 \u00b7 The repair",
  repair: "5 \u00b7 The repair",
  left: "6 \u00b7 After you leave",
  debrief: "7 \u00b7 What happened",
};

type GuideMaker = (title: string, body: string, actionLabel?: string) => MalwareGuide;

function repairGuide(s: State, g: GuideMaker): MalwareGuide {
  if (s.repairView === "download") {
    return g("Choose Run, or close the tab", "The site is offering a file called MindSparkBrowserUpdate.exe.");
  }
  if (s.repairView === "exeBlocked") {
    return g("Choose Show the repair steps, or close the tab", "The file did not run. The site is offering another way.");
  }
  if (s.repairView === "steps") {
    return g("Choose Run these steps, or Close the tab instead", "The site wants you to run a command to repair the browser.");
  }
  if (s.repairView === "cmdBlocked") {
    return g("Press Tell IT", "The attempt was stopped. IT still needs to know what happened.", "Tell IT");
  }
  return g("Close this tab", "IT has been told. You can leave the site now.", "Close this tab");
}

function buildGuide(s: State): MalwareGuide {
  const step = STEP[s.stage];
  const g: GuideMaker = (title, body, actionLabel) => (actionLabel ? { step, title, body, actionLabel } : { step, title, body });

  if (s.replay) {
    return g(
      "Watching the replay",
      "This is what the site would have asked next. Nothing here can run, and you do not need to click anything.",
      "Skip replay",
    );
  }
  if (s.browser.open && s.browser.game && s.browser.active === "news" && inGame(s.stage)) {
    return g("Go back to the MindSpark tab", "The game site is waiting in the other tab.", "MindSpark");
  }
  if (s.stage === "desk") {
    return g("Open Chrome", "Start from the desktop. Click the blinking Chrome icon to open the browser.", "Chrome");
  }
  if (s.stage === "news") {
    return g(
      "Open the IQ Test ad",
      "Between the two stories there is an advertisement for an IQ test. Press Play instantly to open it.",
      "Play instantly",
    );
  }
  if (s.stage === "lobby") {
    return g("Press Play", "The ad opened a new tab with a game site. Press Play to start.", "Play");
  }
  if (s.stage === "notify") {
    return g("Choose Allow, Block, or close the prompt", "The browser is showing a request from this site. Read it, then decide.");
  }
  if (s.stage === "play") {
    if (s.playDone) {
      const label = s.playDone.timedOut ? "Try again" : "Try the next challenge";
      return g(`Press ${label}`, "Your score is in. The site will move on by itself in a moment.", label);
    }
    return g("Pick an answer for each puzzle", "Three quick puzzles against a thirty second clock. Click the tile you think fits.");
  }
  if (s.stage === "offer") {
    if (s.offerView === "listing") {
      return g("Choose Add extension or No thanks", "This page is the upgrade. Read the permissions, then decide.");
    }
    if (s.offerView === "blocked") {
      return g(
        "Press Continue with the basic game",
        "A company control stepped in. The basic game is still there.",
        "Continue with the basic game",
      );
    }
    return g(
      "Press Add the free upgrade",
      "The site says this score is only kept if you add the upgrade. Open it, then decide.",
      "Add the free upgrade",
    );
  }
  if (s.stage === "freeze") {
    if (s.freezePhase !== "frozen") return g("Next round loading", "A new round is starting.");
    return g("Close the tab or press Repair connection", "The page has stopped responding. The browser toolbar above it still works.");
  }
  if (s.stage === "repair") return repairGuide(s, g);
  if (s.stage === "left") {
    if (s.toast) return g("Choose Resolve now or Block this site", "This message is from the game site. You can open it or block it.");
    return g("Back on the desktop", "You left the site. One moment.");
  }
  return g(
    "Press Back to the terminal",
    s.j.sawRepair
      ? "These lines use the choices you made. Read them, then continue."
      : "These lines use the choices you made. You can also watch what the site would have tried next.",
    "Back to the terminal",
  );
}

/* ------------------------------------------------------------------------------------------------
 * Small hooks
 * ---------------------------------------------------------------------------------------------- */

function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

const DESIGN_H = 572;
const DESIGN_MIN_W = 900;

interface Fit {
  s: number;
  w: number;
  h: number;
}

/**
 * The browser is authored at a fixed height and scaled to whatever monitor the 3D scene gives us.
 * Width flexes (the layout is fluid), height scales, so nothing ever needs a page scrollbar.
 */
function useStageFit(el: HTMLDivElement | null): Fit {
  const [fit, setFit] = useState<Fit>({ s: 1, w: 1000, h: DESIGN_H });
  useLayoutEffect(() => {
    if (!el) return undefined;
    const measure = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw < 40 || ch < 40) return;
      const s = Math.min(1.5, Math.max(0.45, Math.min(ch / DESIGN_H, cw / DESIGN_MIN_W)));
      const w = Math.round((cw / s) * 10) / 10;
      const h = Math.round((ch / s) * 10) / 10;
      setFit((cur) => (cur.s === s && cur.w === w && cur.h === h ? cur : { s, w, h }));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return fit;
}

const noop = (): void => undefined;

/* ------------------------------------------------------------------------------------------------
 * Icons (simple SVG, 24px grid)
 * ---------------------------------------------------------------------------------------------- */

type IconName =
  | "lock"
  | "back"
  | "forward"
  | "reload"
  | "star"
  | "puzzle"
  | "user"
  | "dots"
  | "x"
  | "plus"
  | "min"
  | "max"
  | "bell"
  | "bellOff"
  | "shield"
  | "play"
  | "check"
  | "warn"
  | "eye"
  | "pencil"
  | "crown"
  | "file"
  | "download"
  | "folder"
  | "globe"
  | "unlock"
  | "bolt"
  | "save"
  | "info"
  | "spark";

const ICONS: Record<IconName, ReactNode> = {
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </>
  ),
  back: (
    <>
      <path d="M19 12H5" />
      <path d="M11.5 18.5L5 12l6.5-6.5" />
    </>
  ),
  forward: (
    <>
      <path d="M5 12h14" />
      <path d="M12.5 5.5L19 12l-6.5 6.5" />
    </>
  ),
  reload: (
    <>
      <path d="M19.5 12a7.5 7.5 0 1 1-2.4-5.5" />
      <path d="M19.8 4.5v4.6h-4.6" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.8L12 16.8 6.7 19.6l1.1-5.8L3.5 9.7l5.9-.8z" />,
  puzzle: (
    <path d="M9.5 4.5a2 2 0 0 1 4 0V6H17a1 1 0 0 1 1 1v3.5h-1.5a2 2 0 0 0 0 4H18V18a1 1 0 0 1-1 1h-3.5v-1.5a2 2 0 0 0-4 0V19H6a1 1 0 0 1-1-1v-3.5h1.5a2 2 0 0 0 0-4H5V7a1 1 0 0 1 1-1h3.5z" />
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.4" />
      <path d="M5 20c.8-3.6 3.6-5.4 7-5.4s6.2 1.8 7 5.4" />
    </>
  ),
  dots: (
    <>
      <circle cx="12" cy="5.5" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="18.5" r="1.5" fill="currentColor" stroke="none" />
    </>
  ),
  x: <path d="M6.5 6.5l11 11M17.5 6.5l-11 11" />,
  plus: <path d="M12 5.5v13M5.5 12h13" />,
  min: <path d="M6.5 12h11" />,
  max: <rect x="7" y="7" width="10" height="10" rx="1.2" />,
  bell: (
    <>
      <path d="M6.5 16.8h11l-1.5-2.3v-3.9a4.5 4.5 0 0 0-9 0v3.9z" />
      <path d="M10.3 19.6a1.9 1.9 0 0 0 3.4 0" />
    </>
  ),
  bellOff: (
    <>
      <path d="M6.5 16.8h11l-1.5-2.3v-3.9a4.5 4.5 0 0 0-9 0v3.9z" />
      <path d="M10.3 19.6a1.9 1.9 0 0 0 3.4 0" />
      <path d="M4.5 4.5l15 15" />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.2l7 2.6v5.5c0 4.3-2.9 7.6-7 9.5-4.1-1.9-7-5.2-7-9.5V5.8z" />
      <path d="M9 12l2.2 2.2L15.2 10" />
    </>
  ),
  play: (
    <path
      d="M8.5 5.8v12.4a.8.8 0 0 0 1.2.7l9.6-6.2a.8.8 0 0 0 0-1.4L9.7 5.1a.8.8 0 0 0-1.2.7z"
      fill="currentColor"
      stroke="none"
    />
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  warn: (
    <>
      <path d="M12 4l9 15.5H3z" />
      <path d="M12 10v4.3" />
      <circle cx="12" cy="17" r=".9" fill="currentColor" stroke="none" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  pencil: (
    <>
      <path d="M5 19l1-4L16.5 4.5a2 2 0 0 1 2.8 2.8L8.8 17.8z" />
      <path d="M14.5 6.5l3 3" />
    </>
  ),
  crown: <path d="M4 8.5l4 3.5 4-6 4 6 4-3.5-1.5 9h-13z" />,
  file: (
    <>
      <path d="M7 3.5h7l4 4v13H7z" />
      <path d="M14 3.5v4h4" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v10" />
      <path d="M7.5 10.5L12 15l4.5-4.5" />
      <path d="M5 19.5h14" />
    </>
  ),
  folder: (
    <path d="M3.5 7a1.5 1.5 0 0 1 1.5-1.5h4l2 2.2H19A1.5 1.5 0 0 1 20.5 9v8.5A1.5 1.5 0 0 1 19 19H5a1.5 1.5 0 0 1-1.5-1.5z" />
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17" />
      <path d="M12 3.5c2.4 2.5 3.6 5.3 3.6 8.5s-1.2 6-3.6 8.5C9.6 18 8.4 15.2 8.4 12S9.6 6 12 3.5z" />
    </>
  ),
  unlock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.7" />
    </>
  ),
  bolt: <path d="M13 3L5.5 13.5h5.5L10 21l8.5-11H13z" />,
  save: (
    <>
      <path d="M5 4.5h11l3 3v12H5z" />
      <path d="M8 4.5v5h7v-5" />
      <path d="M8 19.5v-5h8v5" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5" />
      <circle cx="12" cy="7.9" r=".9" fill="currentColor" stroke="none" />
    </>
  ),
  spark: (
    <path d="M12 2.8l2.2 6.3 6.3 2.2-6.3 2.2L12 19.8l-2.2-6.3L3.5 11.3l6.3-2.2z" fill="currentColor" stroke="none" />
  ),
};

function Ic({ n, size = 16 }: { n: IconName; size?: number }) {
  return (
    <svg
      className="mlw-ic"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICONS[n]}
    </svg>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Brand bits, shapes and banners
 * ---------------------------------------------------------------------------------------------- */

function Wordmark({ size }: { size: "sm" | "md" }) {
  return (
    <span className={`mlw-wm mlw-wm--${size}`}>
      <span className="mlw-wm-mark">
        <Ic n="spark" size={size === "md" ? 15 : 12} />
      </span>
      <span className="mlw-wm-text">
        {txr("Mind<0>Spark</0>", {}, [<b />])}
      </span>
    </span>
  );
}

type ShapeKind = "circle" | "square" | "triangle" | "diamond" | "hexagon";
type Tone = "blue" | "amber" | "coral" | "ink";

interface ShapeSpec {
  kind: ShapeKind;
  tone: Tone;
  rot?: number;
}

const shape = (kind: ShapeKind, tone: Tone, rot = 0): ShapeSpec => ({ kind, tone, rot });

function Shape({ spec, size }: { spec: ShapeSpec; size: number }) {
  const style: CSSProperties | undefined = spec.rot ? { transform: `rotate(${spec.rot}deg)` } : undefined;
  return (
    <svg className={`mlw-sh mlw-sh--${spec.tone}`} viewBox="0 0 40 40" width={size} height={size} style={style} aria-hidden="true">
      {spec.kind === "circle" ? <circle cx="20" cy="20" r="14" /> : null}
      {spec.kind === "square" ? <rect x="7" y="7" width="26" height="26" rx="5" /> : null}
      {spec.kind === "triangle" ? <path d="M20 6.5L34 32H6z" /> : null}
      {spec.kind === "diamond" ? <path d="M20 5.5L34.5 20 20 34.5 5.5 20z" /> : null}
      {spec.kind === "hexagon" ? <path d="M20 5l13 7.5v15L20 35 7 27.5v-15z" /> : null}
    </svg>
  );
}

function shapeLabel(spec: ShapeSpec): string {
  return `${spec.kind}`;
}

const PIP_POS: Record<number, ReadonlyArray<readonly [number, number]>> = {
  1: [[50, 50]],
  2: [
    [30, 30],
    [70, 70],
  ],
  3: [
    [26, 26],
    [50, 50],
    [74, 74],
  ],
  4: [
    [30, 30],
    [70, 30],
    [30, 70],
    [70, 70],
  ],
  5: [
    [28, 28],
    [72, 28],
    [50, 50],
    [28, 72],
    [72, 72],
  ],
  6: [
    [30, 24],
    [70, 24],
    [30, 50],
    [70, 50],
    [30, 76],
    [70, 76],
  ],
};

function Pips({ n }: { n: number }) {
  const pos: ReadonlyArray<readonly [number, number]> = PIP_POS[n] ?? [];
  return (
    <svg className="mlw-pips" viewBox="0 0 100 100" aria-hidden="true">
      {pos.map(([x, y]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r="9.5" />
      ))}
    </svg>
  );
}

/** The slim yellow and navy system banner a company control shows. Calm, not a scream. */
function SysBanner({ children }: { children: ReactNode }) {
  return (
    <div className="mlw-sys" role="alert">
      <span className="mlw-sys-tag">
        <Ic n="shield" size={15} />
        {tx("NorthBay IT")}
      </span>
      <p className="mlw-sys-text">{children}</p>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="mlw-skel" aria-hidden="true">
      <div className="mlw-skel-nav">
        <i style={{ width: 118 }} />
        <i style={{ width: 54 }} />
        <i style={{ width: 54 }} />
      </div>
      <div className="mlw-skel-hero">
        <i style={{ width: 120, height: 12 }} />
        <i style={{ width: 430, height: 34 }} />
        <i style={{ width: 380, height: 14 }} />
        <i style={{ width: 210, height: 58, borderRadius: 16 }} />
      </div>
      <div className="mlw-skel-row">
        <i />
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Chrome window
 * ---------------------------------------------------------------------------------------------- */

interface TabSpec {
  id: TabId;
  title: string;
  loading: boolean;
  leaving: boolean;
}

function Favicon({ id }: { id: TabId }) {
  if (id === "news") {
    return (
      <span className="mlw-fav mlw-fav--news" aria-hidden="true">
        {tx("H")}
      </span>
    );
  }
  return (
    <span className="mlw-fav mlw-fav--game" aria-hidden="true">
      <Ic n="spark" size={9} />
    </span>
  );
}

const BOOKMARKS: ReadonlyArray<{ icon: IconName; label: string }> = [
  { icon: "folder", label: "NorthBay Intranet" },
  { icon: "globe", label: "IT Helpdesk" },
  { icon: "globe", label: "Travel desk" },
  { icon: "globe", label: "Weather" },
  { icon: "globe", label: "Staff directory" },
];

function ChromeWindow({
  tabs,
  active,
  closing,
  busy,
  bell,
  bubble,
  locked = false,
  onSelect,
  onCloseTab,
  onCloseWindow,
  onReload,
  children,
}: {
  tabs: TabSpec[];
  active: TabId;
  closing: boolean;
  busy: boolean;
  bell: boolean;
  bubble: ReactNode;
  locked?: boolean;
  onSelect: (id: TabId) => void;
  onCloseTab: (id: TabId) => void;
  onCloseWindow: () => void;
  onReload: () => void;
  children: ReactNode;
}) {
  const site = SITE[active];
  const [editing, setEditing] = useState(false);
  return (
    <div className={`mlw-win${closing ? " mlw-is-closing" : ""}${locked ? " mlw-is-locked" : ""}`}>
      <div className="mlw-strip">
        <div className="mlw-tabs" role="tablist">
          {tabs.map((t) => (
            <div
              key={t.id}
              className={`mlw-tab${t.id === active ? " mlw-is-active" : ""}${t.leaving ? " mlw-is-leaving" : ""}`}
              role="presentation"
            >
              <button
                type="button"
                role="tab"
                aria-selected={t.id === active}
                className="mlw-tab-main"
                disabled={locked}
                onClick={() => onSelect(t.id)}
              >
                {t.loading ? <span className="mlw-tab-spin" aria-hidden="true" /> : <Favicon id={t.id} />}
                <span className="mlw-tab-title">{t.title}</span>
              </button>
              <button type="button" className="mlw-tab-x" aria-label={tx("Close tab")} disabled={locked} onClick={() => onCloseTab(t.id)}>
                <Ic n="x" size={12} />
              </button>
            </div>
          ))}
          <span className="mlw-newtab" aria-hidden="true">
            <Ic n="plus" size={14} />
          </span>
        </div>
        <div className="mlw-wc">
          <span className="mlw-wc-b" aria-hidden="true">
            <Ic n="min" size={14} />
          </span>
          <span className="mlw-wc-b" aria-hidden="true">
            <Ic n="max" size={13} />
          </span>
          <button type="button" className="mlw-wc-b mlw-wc-close" aria-label={tx("Close window")} disabled={locked} onClick={onCloseWindow}>
            <Ic n="x" size={15} />
          </button>
        </div>
      </div>

      <div className="mlw-toolbar">
        <button type="button" className="mlw-nb" aria-label={tx("Back")} disabled>
          <Ic n="back" size={17} />
        </button>
        <button type="button" className="mlw-nb" aria-label={tx("Forward")} disabled>
          <Ic n="forward" size={17} />
        </button>
        <button type="button" className={`mlw-nb${busy ? " mlw-is-busy" : ""}`} aria-label={tx("Reload")} disabled={locked} onClick={onReload}>
          <Ic n="reload" size={17} />
        </button>
        <div className="mlw-omni-wrap">
          <div
            className={`mlw-omni${bubble ? " mlw-is-asking" : ""}${editing ? " mlw-is-editing" : ""}`}
            onClick={() => {
              if (!locked) setEditing(true);
            }}
          >
            <span className="mlw-lock" aria-hidden="true">
              <Ic n="lock" size={14} />
            </span>
            {editing ? (
              <input
                className="mlw-url-input"
                aria-label={tx("Address bar")}
                readOnly
                autoFocus
                spellCheck={false}
                value={`https://${site.host}${site.path}`}
                onFocus={(e) => e.currentTarget.select()}
                onBlur={() => setEditing(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
                }}
              />
            ) : (
              <span className="mlw-url">
                <span className="mlw-url-dim">https://</span>
                <span>{site.host}</span>
                <span className="mlw-url-dim">{site.path}</span>
              </span>
            )}
            {bell ? (
              <span className="mlw-omni-bell" aria-hidden="true">
                <Ic n="bellOff" size={15} />
              </span>
            ) : null}
            <span className="mlw-omni-star" aria-hidden="true">
              <Ic n="star" size={15} />
            </span>
          </div>
          {bubble}
        </div>
        <div className="mlw-tools" aria-hidden="true">
          <span className="mlw-tool">
            <Ic n="puzzle" size={17} />
          </span>
          <span className="mlw-tool mlw-tool--me">
            <Ic n="user" size={15} />
          </span>
          <span className="mlw-tool">
            <Ic n="dots" size={17} />
          </span>
        </div>
      </div>

      <div className="mlw-bm" aria-hidden="true">
        {BOOKMARKS.map((b) => (
          <span key={b.label} className="mlw-bm-i">
            <Ic n={b.icon} size={13} />
            {b.label}
          </span>
        ))}
      </div>
      <div className={`mlw-loadbar${busy ? " mlw-is-on" : ""}`} aria-hidden="true" />
      <div className="mlw-pages">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Page 1: Harbor Daily
 * ---------------------------------------------------------------------------------------------- */

const SECTIONS = ["Local", "Business", "Harbor & Sea", "Sport", "Culture", "Opinion"];

const MOST_READ = [
  "Why the lighthouse lamp goes dark for a week",
  "Rail talks resume on Thursday",
  "Ten walks to take before the summer crowds",
  "Rowing club wins the Estuary Cup",
  "Photos: market day at the old dock",
];

function AdCard({ mode, onAd }: { mode: AdMode; onAd: () => void }) {
  return (
    <aside className={`mlw-ad${mode === "used" ? " mlw-is-used" : ""}`} aria-label={tx("Advertisement")}>
      <span className="mlw-ad-tag">{tx("Ad")}</span>
      <div className="mlw-ad-art" aria-hidden="true">
        <i className="mlw-ad-a" />
        <i className="mlw-ad-b" />
        <i className="mlw-ad-c" />
      </div>
      <div className="mlw-ad-copy">
        <h3 className="mlw-ad-h">{tx("IQ Test — Find out how smart you really are?")}</h3>
        <p className="mlw-ad-p">{tx("Play instantly. No download.")}</p>
      </div>
      <button
        type="button"
        className={`mlw-ad-cta${mode === "live" ? " mlw-pulse" : ""}`}
        disabled={mode === "used"}
        onClick={onAd}
      >
        <Ic n="play" size={13} />
        {tx("Play instantly")}
      </button>
    </aside>
  );
}

function NewsPage({ adMode, onAd }: { adMode: AdMode; onAd: () => void }) {
  return (
    <div className="mlw-news">
      <div className="mlw-news-util">
        <span>{tx("Wednesday 24 June 2026")}</span>
        <span className="mlw-news-util-r">
          <span>{tx("Sign in")}</span>
          <span className="mlw-news-sub">{tx("Subscribe")}</span>
        </span>
      </div>
      <header className="mlw-news-mast">
        <span className="mlw-news-ed">{tx("Harbor Edition")}</span>
        <h1 className="mlw-news-title">{tx("Harbor Daily")}</h1>
        <span className="mlw-news-wx">{tx("17°C · Light showers")}</span>
      </header>
      <nav className="mlw-news-nav" aria-label={tx("Sections")}>
        {SECTIONS.map((x) => (
          <span key={x}>{x}</span>
        ))}
      </nav>
      <div className="mlw-news-body">
        <div className="mlw-news-main">
          <article className="mlw-art">
            <span className="mlw-art-kick">{tx("Local")}</span>
            <h2 className="mlw-art-h">{tx("Council backs a cycle path along the old quay")}</h2>
            <p className="mlw-art-p">
              {tx("The route will link the fish market to the station and is due to open before the autumn regatta, planners said.")}
            </p>
            <span className="mlw-art-by">{tx("By Ines Marlow · 12 min ago")}</span>
          </article>
          <AdCard mode={adMode} onAd={onAd} />
          <article className="mlw-art">
            <span className="mlw-art-kick">{tx("Business")}</span>
            <h2 className="mlw-art-h">{tx("Harbour bakery opens ninth shop on Mill Street")}</h2>
            <p className="mlw-art-p">
              {tx("Owners say demand for sourdough has doubled since last year, and every loaf is still baked on site.")}
            </p>
            <span className="mlw-art-by">{tx("By Tomas Reeve · 1 hr ago")}</span>
          </article>
        </div>
        <aside className="mlw-news-side">
          <h3 className="mlw-side-h">{tx("Most read")}</h3>
          <ol className="mlw-most">
            {MOST_READ.map((t, n) => (
              <li key={t}>
                <b>{n + 1}</b>
                <span>{t}</span>
              </li>
            ))}
          </ol>
          <div className="mlw-tide">
            <span className="mlw-tide-h">{tx("Tides today")}</span>
            <span>{tx("High 11:42 · Low 17:58")}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Page 2: MindSpark lobby
 * ---------------------------------------------------------------------------------------------- */

const BOARD: ReadonlyArray<{ name: string; score: string; tone: Tone }> = [
  { name: "Priya N.", score: "2,480", tone: "blue" },
  { name: "Jonas K.", score: "2,310", tone: "amber" },
  { name: "Mei L.", score: "2,275", tone: "coral" },
  { name: "Tomas R.", score: "2,140", tone: "ink" },
  { name: "Amara D.", score: "2,090", tone: "blue" },
];

function LobbyPage({ starting, asking, onPlay }: { starting: boolean; asking: boolean; onPlay: () => void }) {
  const busy = starting || asking;
  return (
    <div className={`mlw-lobby${asking ? " mlw-is-asking" : ""}`}>
      <header className="mlw-lobby-nav">
        <Wordmark size="md" />
        <nav className="mlw-lobby-links" aria-label={tx("Site")}>
          <span>{tx("Games")}</span>
          <span>{tx("Daily")}</span>
          <span>{tx("Leaderboard")}</span>
        </nav>
        <span className="mlw-lobby-signin">{tx("Sign in")}</span>
      </header>
      <section className="mlw-hero">
        <i className="mlw-deco mlw-deco--a" aria-hidden="true" />
        <i className="mlw-deco mlw-deco--b" aria-hidden="true" />
        <i className="mlw-deco mlw-deco--c" aria-hidden="true" />
        <i className="mlw-deco mlw-deco--d" aria-hidden="true" />
        <span className="mlw-hero-kick">{tx("Today’s brain game")}</span>
        <h1 className="mlw-hero-h">{tx("Quick puzzles. Real bragging rights.")}</h1>
        <p className="mlw-hero-p">
          {tx("Solve three short visual puzzles against a 30-second clock and see where you land on today’s board.")}
        </p>
        <button
          type="button"
          className={`mlw-play-btn${busy ? " mlw-is-busy" : " mlw-pulse"}`}
          disabled={busy}
          onClick={onPlay}
        >
          {busy ? (
            <span className="mlw-spin" aria-hidden="true" />
          ) : (
            <span className="mlw-play-ic" aria-hidden="true">
              <Ic n="play" size={17} />
            </span>
          )}
          {tx("Play")}
        </button>
        <span className="mlw-hero-note">{tx("Free · 3 puzzles · about a minute")}</span>
      </section>
      <section className="mlw-board" aria-label={tx("Today's top scores")}>
        <div className="mlw-board-h">
          <span>
            <Ic n="crown" size={14} /> {tx("Today’s top scores")}
          </span>
          <span className="mlw-board-live">{tx("Live")}</span>
        </div>
        <ol className="mlw-board-row">
          {BOARD.map((p, n) => (
            <li key={p.name} className="mlw-pl" style={{ animationDelay: `${160 + n * 70}ms` }}>
              <span className="mlw-pl-rank">{n + 1}</span>
              <span className={`mlw-pl-av mlw-pl-av--${p.tone}`}>{p.name.charAt(0)}</span>
              <span className="mlw-pl-main">
                <span className="mlw-pl-name">{p.name}</span>
                <span className="mlw-pl-score">{p.score}</span>
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Page 3: the puzzle round (original puzzles, 30 second clock)
 * ---------------------------------------------------------------------------------------------- */

const TIME_LIMIT = 30;

type Puzzle =
  | { id: "seq"; prompt: string; hint: string; cells: ShapeSpec[]; options: ShapeSpec[]; answer: number }
  | { id: "grid"; prompt: string; hint: string; cells: ReadonlyArray<number | null>; options: number[]; answer: number }
  | { id: "odd"; prompt: string; hint: string; options: ShapeSpec[]; answer: number };

const PUZZLES: readonly Puzzle[] = [
  {
    id: "seq",
    prompt: "What comes next?",
    hint: "The shapes follow a repeating pattern.",
    cells: [shape("circle", "blue"), shape("square", "amber"), shape("triangle", "coral"), shape("circle", "blue"), shape("square", "amber")],
    options: [shape("square", "amber"), shape("diamond", "ink"), shape("triangle", "coral"), shape("circle", "blue")],
    answer: 2,
  },
  {
    id: "grid",
    prompt: "Which tile completes the grid?",
    hint: "Look along the rows and down the columns.",
    cells: [1, 2, 3, 2, 3, 4, 3, 4, null],
    options: [3, 5, 4, 6],
    answer: 1,
  },
  {
    id: "odd",
    prompt: "Which one does not belong?",
    hint: "Colour and size can mislead you. Look at the shape.",
    options: [shape("hexagon", "blue", 8), shape("hexagon", "amber", -10), shape("hexagon", "ink", 4), shape("circle", "coral")],
    answer: 3,
  },
];

function TimerRing({ left }: { left: number }) {
  return (
    <div className={`mlw-timer${left <= 8 ? " mlw-is-low" : ""}`} role="timer" aria-label={tx("{left} seconds left", { left })}>
      <svg className="mlw-timer-svg" viewBox="0 0 44 44" aria-hidden="true">
        <circle className="mlw-timer-track" cx="22" cy="22" r="18" />
        <circle className="mlw-timer-arc" cx="22" cy="22" r="18" style={{ animationDuration: `${TIME_LIMIT}s` }} />
      </svg>
      <span className="mlw-timer-n">{left}</span>
    </div>
  );
}

function PlayRound({ onDone }: { onDone: (correct: number, timedOut: boolean) => void }) {
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [left, setLeft] = useState(TIME_LIMIT);
  const correctRef = useRef(0);
  const doneRef = useRef(false);
  const onDoneRef = useLatest(onDone);

  const puzzle = PUZZLES[index];
  const verdict: "ok" | "bad" | null = picked === null ? null : picked === puzzle.answer ? "ok" : "bad";

  const finish = useCallback(
    (timedOut: boolean) => {
      if (doneRef.current) return;
      doneRef.current = true;
      onDoneRef.current(correctRef.current, timedOut);
    },
    [onDoneRef],
  );

  useEffect(() => {
    const t0 = performance.now();
    const id = window.setInterval(() => {
      const remain = TIME_LIMIT - (performance.now() - t0) / 1000;
      if (remain <= 0) {
        window.clearInterval(id);
        setLeft(0);
        finish(true);
        return;
      }
      setLeft(Math.ceil(remain));
    }, 200);
    return () => window.clearInterval(id);
  }, [finish]);

  useEffect(() => {
    if (picked === null) return undefined;
    const ok = picked === PUZZLES[index].answer;
    const id = window.setTimeout(
      () => {
        if (index + 1 >= PUZZLES.length) {
          finish(false);
          return;
        }
        setIndex(index + 1);
        setPicked(null);
      },
      ok ? 760 : 1100,
    );
    return () => window.clearTimeout(id);
  }, [picked, index, finish]);

  const choose = (n: number) => {
    if (picked !== null || doneRef.current) return;
    if (n === puzzle.answer) correctRef.current += 1;
    setPicked(n);
  };

  const optClass = (n: number): string => {
    if (picked === null) return "mlw-opt";
    if (n === picked) return `mlw-opt ${verdict === "ok" ? "mlw-is-ok" : "mlw-is-bad"}`;
    if (n === puzzle.answer) return "mlw-opt mlw-is-answer";
    return "mlw-opt mlw-is-dim";
  };

  let stage: ReactNode = null;
  if (puzzle.id === "seq") {
    const fill = picked === null ? null : puzzle.options[puzzle.answer];
    stage = (
      <div className="mlw-q-body">
        <div className="mlw-seq" aria-label={tx("Shape sequence")}>
          {puzzle.cells.map((c, n) => (
            <span key={`${c.kind}-${n}`} className="mlw-cell">
              <Shape spec={c} size={36} />
            </span>
          ))}
          <span className={`mlw-cell mlw-cell--gap${fill ? " mlw-is-filled" : ""}`}>
            {fill ? <Shape spec={fill} size={36} /> : <b>?</b>}
          </span>
        </div>
        <div className="mlw-opts mlw-opts--row">
          {puzzle.options.map((o, n) => (
            <button
              key={`${o.kind}-${n}`}
              type="button"
              className={optClass(n)}
              disabled={picked !== null}
              aria-label={shapeLabel(o)}
              onClick={() => choose(n)}
            >
              <Shape spec={o} size={40} />
              {picked !== null && n === picked ? <span className="mlw-opt-mark">{verdict === "ok" ? <Ic n="check" size={14} /> : <Ic n="x" size={14} />}</span> : null}
            </button>
          ))}
        </div>
      </div>
    );
  } else if (puzzle.id === "grid") {
    const fill = picked === null ? null : puzzle.options[puzzle.answer];
    stage = (
      <div className="mlw-q-body mlw-q-body--split">
        <div className="mlw-grid" aria-label={tx("Three by three pattern grid")}>
          {puzzle.cells.map((c, n) => (
            <span key={`g${n}`} className={`mlw-gcell${c === null ? " mlw-gcell--gap" : ""}${c === null && fill ? " mlw-is-filled" : ""}`}>
              {c === null ? fill ? <Pips n={fill} /> : <b>?</b> : <Pips n={c} />}
            </span>
          ))}
        </div>
        <div className="mlw-opts mlw-opts--grid">
          {puzzle.options.map((o, n) => (
            <button
              key={`o${o}`}
              type="button"
              className={optClass(n)}
              disabled={picked !== null}
              aria-label={tx("{o} dots", { o })}
              onClick={() => choose(n)}
            >
              <Pips n={o} />
              {picked !== null && n === picked ? <span className="mlw-opt-mark">{verdict === "ok" ? <Ic n="check" size={14} /> : <Ic n="x" size={14} />}</span> : null}
            </button>
          ))}
        </div>
      </div>
    );
  } else {
    stage = (
      <div className="mlw-q-body">
        <div className="mlw-opts mlw-opts--odd">
          {puzzle.options.map((o, n) => (
            <button
              key={`${o.kind}-${n}`}
              type="button"
              className={`${optClass(n)} mlw-opt--big`}
              disabled={picked !== null}
              aria-label={tx("Shape {v}", { v: n + 1 })}
              onClick={() => choose(n)}
            >
              <Shape spec={o} size={62} />
              {picked !== null && n === picked ? <span className="mlw-opt-mark">{verdict === "ok" ? <Ic n="check" size={14} /> : <Ic n="x" size={14} />}</span> : null}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`mlw-play${verdict ? ` mlw-play--${verdict}` : ""}`}>
      <header className="mlw-play-top">
        <Wordmark size="sm" />
        <div className="mlw-prog" aria-label={tx("Puzzle {v} of {length}", { v: index + 1, length: PUZZLES.length })}>
          {PUZZLES.map((p, n) => (
            <span key={p.id} className={`mlw-prog-d${n < index ? " mlw-is-done" : ""}${n === index ? " mlw-is-now" : ""}`} />
          ))}
          <span className="mlw-prog-t">
            {txr("Puzzle {v} of {length}", { v: index + 1, length: PUZZLES.length })}
          </span>
        </div>
        <TimerRing left={left} />
      </header>
      <section className="mlw-q" key={puzzle.id}>
        <h2 className="mlw-q-h">{puzzle.prompt}</h2>
        <p className="mlw-q-hint">{puzzle.hint}</p>
        {stage}
      </section>
    </div>
  );
}

function ResultCard({ done, onNext }: { done: { correct: number; timedOut: boolean }; onNext: () => void }) {
  const ok = !done.timedOut;
  const circ = 2 * Math.PI * 40;
  const off = circ * (1 - done.correct / PUZZLES.length);
  const ringStyle = { "--mlw-circ": circ.toFixed(1), strokeDasharray: circ.toFixed(1), strokeDashoffset: off.toFixed(1) } as CSSProperties;
  return (
    <div className="mlw-result">
      <div className="mlw-result-card">
        <div className="mlw-score" aria-label={tx("{correct} out of {length} correct", { correct: done.correct, length: PUZZLES.length })}>
          <svg viewBox="0 0 100 100" className="mlw-score-svg" aria-hidden="true">
            <circle className="mlw-score-track" cx="50" cy="50" r="40" />
            <circle className={`mlw-score-arc${ok ? "" : " mlw-is-late"}`} cx="50" cy="50" r="40" style={ringStyle} />
          </svg>
          <span className="mlw-score-n">
            {done.correct}
            <small>/ {PUZZLES.length}</small>
          </span>
        </div>
        <h2 className="mlw-result-h">{ok ? tx("Good run.") : tx("Time's up")}</h2>
        <p className="mlw-result-p">{ok ? tx("Try the next challenge?") : tx("Try again?")}</p>
        <button type="button" className="mlw-btn mlw-btn--blue mlw-btn--xl mlw-pulse" onClick={onNext}>
          {ok ? tx("Try the next challenge") : tx("Try again")}
        </button>
        <span className="mlw-result-bar" aria-hidden="true">
          <i />
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Page 4: the offer and the extension listing
 * ---------------------------------------------------------------------------------------------- */

function OfferPage({
  view,
  onSee,
  onAdd,
  onContinue,
}: {
  view: OfferView;
  onSee: () => void;
  onAdd: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="mlw-offer">
      <div className={`mlw-offer-card${view !== "offer" ? " mlw-is-back" : ""}`}>
        <div className="mlw-offer-head">
          <span className="mlw-offer-ic" aria-hidden="true">
            <Ic n="unlock" size={24} />
          </span>
          <span className="mlw-offer-badge">{tx("Free upgrade")}</span>
        </div>
        <div className="mlw-offer-body">
          <h2 className="mlw-offer-h">{tx("Unlock Challenge Mode")}</h2>
          <p className="mlw-offer-p">{tx("Your score is not saved yet. Add the free upgrade to keep it, unlock extra levels, and start a daily streak.")}</p>
          <ul className="mlw-perks">
            <li>
              <Ic n="save" size={15} /> {tx("Saved score")}
            </li>
            <li>
              <Ic n="bolt" size={15} /> {tx("Extra levels")}
            </li>
            <li>
              <Ic n="save" size={15} /> {tx("Daily streak")}
            </li>
          </ul>
          {view === "offer" ? (
            <div className="mlw-offer-actions">
              <button type="button" className="mlw-btn mlw-btn--blue mlw-btn--xl mlw-pulse" onClick={onSee}>
                {tx("Add the free upgrade")}
              </button>
            </div>
          ) : (
            <div className="mlw-offer-actions mlw-offer-actions--ghost" aria-hidden="true" />
          )}
        </div>
      </div>

      {view !== "offer" ? (
        <div className="mlw-scrim">
          {view === "listing" ? (
            <div className="mlw-sheet" role="dialog" aria-label={tx("Challenge Mode upgrade")}>
              <div className="mlw-ext-crumb">{tx("Add-ons &rsaquo; Games &rsaquo; Challenge Mode")}</div>
              <div className="mlw-ext-head">
                <span className="mlw-ext-ic" aria-hidden="true">
                  <Ic n="puzzle" size={32} />
                </span>
                <div className="mlw-ext-id">
                  <h2 className="mlw-ext-h">
                    {txr("Challenge Mode <0>by MindSpark</0>", {}, [<span />])}
                  </h2>
                  <p className="mlw-ext-meta">{tx("Free upgrade · Games · 40,000+ users")}</p>
                </div>
                <div className="mlw-ext-cta">
                  <button type="button" className="mlw-btn mlw-btn--blue" onClick={onAdd}>
                    {tx("Add extension")}
                  </button>
                  <button type="button" className="mlw-btn mlw-btn--ghost" onClick={onContinue}>
                    {tx("No thanks")}
                  </button>
                </div>
              </div>
              <p className="mlw-ext-desc">{tx("Saves your score, unlocks extra levels, and keeps a daily streak.")}</p>
              <div className="mlw-ext-perm">
                <h3 className="mlw-ext-ph">{tx("Permissions")}</h3>
                <ul>
                  <li>
                    {txr("{span}Read and change data on websites you visit.", { span: <span className="mlw-ext-pi" aria-hidden="true">
                      <Ic n="pencil" size={15} />
                    </span> })}
                  </li>
                  <li>
                    {txr("{span}Know which site you are on.", { span: <span className="mlw-ext-pi" aria-hidden="true">
                      <Ic n="eye" size={15} />
                    </span> })}
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="mlw-sheet mlw-sheet--block">
              <div className="mlw-block-head">
                <span className="mlw-block-ic" aria-hidden="true">
                  <Ic n="puzzle" size={20} />
                </span>
                <span className="mlw-block-name">{tx("Challenge Mode by MindSpark")}</span>
                <span className="mlw-block-state">{tx("Not added")}</span>
              </div>
              <SysBanner>{tx("NorthBay blocked an unapproved extension. You can keep playing the basic game.")}</SysBanner>
              <div className="mlw-block-actions">
                <button type="button" className="mlw-btn mlw-btn--blue mlw-btn--lg mlw-pulse" onClick={onContinue}>
                  {tx("Continue with the basic game")}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Page 5: the next round freezes
 * ---------------------------------------------------------------------------------------------- */

function FreezePage({
  phase,
  locked = false,
  pressing = false,
  onRepair,
}: {
  phase: FreezePhase;
  locked?: boolean;
  pressing?: boolean;
  onRepair: () => void;
}) {
  const tiles = Array.from({ length: 12 }, (_, n) => n);
  return (
    <div className={`mlw-fz mlw-fz--${phase}`}>
      <div className="mlw-fz-top">
        <Wordmark size="sm" />
        <span className="mlw-fz-round">{tx("Round 2")}</span>
        <span className="mlw-fz-score">{tx("Score 1,240")}</span>
      </div>
      <div className="mlw-fz-board" aria-hidden="true">
        {tiles.map((n) => (
          <i key={n} className="mlw-fz-tile" style={{ "--mlw-i": n } as CSSProperties} />
        ))}
      </div>
      <div className="mlw-fz-meter" aria-hidden="true">
        <i />
      </div>
      {phase === "frozen" ? (
        <div className="mlw-fz-veil">
          <div className="mlw-modal" role="alertdialog" aria-label={tx("Browser compatibility problem")}>
            <span className="mlw-modal-ic" aria-hidden="true">
              <Ic n="warn" size={26} />
            </span>
            <h2 className="mlw-modal-h">{tx("Browser compatibility problem")}</h2>
            <p className="mlw-modal-p">{tx("Your progress is saved. Repair the connection to continue.")}</p>
            <button
              type="button"
              className={`mlw-btn mlw-btn--blue mlw-btn--lg${pressing ? " mlw-ghost" : ""}`}
              disabled={locked}
              onClick={onRepair}
            >
              {tx("Repair connection")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Page 6: the repair that is not one
 * ---------------------------------------------------------------------------------------------- */

function RepairPage({
  view,
  locked = false,
  ghost,
  onRun,
  onShowSteps,
  onRunSteps,
  onTellIT,
  onCloseTab,
}: {
  view: RepairView;
  locked?: boolean;
  ghost: Ghost | null;
  onRun: () => void;
  onShowSteps: () => void;
  onRunSteps: () => void;
  onTellIT: () => void;
  onCloseTab: () => void;
}) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (view !== "download") {
      setReady(true);
      return undefined;
    }
    setReady(false);
    const id = window.setTimeout(() => setReady(true), 1000);
    return () => window.clearTimeout(id);
  }, [view]);

  const blocked = view !== "download";
  const showFile = view === "download" || view === "exeBlocked";
  const showTerm = view === "steps" || view === "cmdBlocked";
  const step = showFile ? 1 : 2;
  const press = (g: Ghost) => (ghost === g ? " mlw-ghost" : "");

  return (
    <div className="mlw-rep">
      <div className="mlw-rep-col">
        <header className="mlw-rep-head">
          <Wordmark size="sm" />
          <ol className="mlw-rep-steps" aria-label={tx("Progress")}>
            <li className={step === 1 ? "mlw-is-on" : "mlw-is-done"}>{tx("Repair tool")}</li>
            <li className={step === 2 ? "mlw-is-on" : ""}>{tx("Quick repair")}</li>
          </ol>
        </header>
        <div className="mlw-rep-title">
          <h2 className="mlw-rep-h">{tx("Repair connection")}</h2>
          <p className="mlw-rep-sub">{tx("Two quick steps to get back to your game.")}</p>
        </div>

        {showFile ? (
          <div className={`mlw-dl${blocked ? " mlw-is-blocked" : ""}${ready ? " mlw-is-ready" : ""}`}>
            <span className="mlw-dl-ic" aria-hidden="true">
              <Ic n={blocked ? "shield" : "file"} size={22} />
            </span>
            <div className="mlw-dl-main">
              <span className="mlw-dl-name">MindSparkBrowserUpdate.exe</span>
              <span className="mlw-dl-meta">
                {blocked ? tx("Blocked by your organisation") : ready ? tx("mindspark.example · 1.8 MB · Downloaded") : tx("mindspark.example · Downloading…")}
              </span>
              <span className="mlw-dl-bar" aria-hidden="true">
                <i />
              </span>
            </div>
            <button
              type="button"
              className={`mlw-btn mlw-btn--blue${press("run")}`}
              disabled={locked || blocked || !ready}
              onClick={onRun}
            >
              {tx("Run")}
            </button>
          </div>
        ) : null}

        {view === "exeBlocked" ? (
          <>
            <SysBanner>{tx("Your organisation blocked this file. It did not run.")}</SysBanner>
            <div className="mlw-alt">
              <div className="mlw-alt-t">
                <h3 className="mlw-alt-h">{tx("Installation unavailable?")}</h3>
                <p className="mlw-alt-p">{tx("Use the quick browser repair.")}</p>
              </div>
              <button
                type="button"
                className={`mlw-btn mlw-btn--blue${press("steps")}`}
                disabled={locked}
                onClick={onShowSteps}
              >
                {tx("Show the repair steps")}
              </button>
            </div>
          </>
        ) : null}

        {showTerm ? (
          <>
            <div className={`mlw-term${view === "cmdBlocked" ? " mlw-is-blocked" : ""}`} aria-label={tx("Repair steps shown as example text")}>
              <div className="mlw-term-bar">
                <i />
                <i />
                <i />
                <span>{tx("Quick browser repair")}</span>
              </div>
              <div className="mlw-term-body">
                <span className="mlw-term-ps">{tx("C:\\Users\\you>")}</span>
                <span className="mlw-term-cmd">{tx("mindspark-setup --check")}</span>
                <span className="mlw-term-caret" />
              </div>
            </div>
            {view === "steps" ? (
              <div className="mlw-rep-actions">
                <button
                  type="button"
                  className={`mlw-btn mlw-btn--blue mlw-btn--lg${press("runSteps")}`}
                  disabled={locked}
                  onClick={onRunSteps}
                >
                  {tx("Run these steps")}
                </button>
                <button type="button" className="mlw-btn mlw-btn--ghost mlw-btn--lg" disabled={locked} onClick={onCloseTab}>
                  {tx("Close the tab instead")}
                </button>
              </div>
            ) : (
              <>
                <SysBanner>{tx("Your organisation blocked the command. Tell IT what happened, even though the attempt was stopped.")}</SysBanner>
                <div className="mlw-rep-actions">
                  <button
                    type="button"
                    className={`mlw-btn mlw-btn--navy mlw-btn--lg${locked ? "" : " mlw-pulse"}`}
                    disabled={locked}
                    onClick={onTellIT}
                  >
                    {tx("Tell IT")}
                  </button>
                </div>
              </>
            )}
          </>
        ) : null}

        {view === "reported" ? (
          <div className="mlw-done">
            <span className="mlw-done-ic" aria-hidden="true">
              <Ic n="check" size={24} />
            </span>
            <h3 className="mlw-done-h">{tx("IT has been told")}</h3>
            <p className="mlw-done-p">{tx("Thank you for reporting it, even though the attempt was stopped.")}</p>
            <button type="button" className="mlw-btn mlw-btn--blue mlw-btn--lg mlw-pulse" onClick={onCloseTab}>
              {tx("Close this tab")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Browser permission bubble and the Windows toast
 * ---------------------------------------------------------------------------------------------- */

function PermissionBubble({ onAnswer }: { onAnswer: (v: Answer) => void }) {
  const [leaving, setLeaving] = useState<Answer | null>(null);
  const onAnswerRef = useLatest(onAnswer);
  useEffect(() => {
    if (!leaving) return undefined;
    const id = window.setTimeout(() => onAnswerRef.current(leaving), 170);
    return () => window.clearTimeout(id);
  }, [leaving, onAnswerRef]);
  const pick = (v: Answer) => setLeaving((cur) => cur ?? v);
  return (
    <div className={`mlw-bubble${leaving ? " mlw-is-leaving" : ""}`} role="dialog" aria-label={tx("Permission request")}>
      <span className="mlw-bubble-arrow" aria-hidden="true" />
      <button type="button" className="mlw-bubble-x" aria-label={tx("Dismiss")} onClick={() => pick("dismissed")}>
        <Ic n="x" size={14} />
      </button>
      <div className="mlw-bubble-head">
        <span className="mlw-bubble-site" aria-hidden="true">
          <Ic n="spark" size={11} />
        </span>
        <span>{tx("mindspark.example wants to")}</span>
      </div>
      <div className="mlw-bubble-perm">
        <Ic n="bell" size={17} />
        <strong>{tx("Show notifications")}</strong>
      </div>
      <p className="mlw-bubble-note">{tx("Enable notifications to receive your results and unlock daily challenges.")}</p>
      <div className="mlw-bubble-btns">
        <button type="button" className="mlw-bubble-btn mlw-bubble-btn--allow" onClick={() => pick("allowed")}>
          {tx("Allow")}
        </button>
        <button type="button" className="mlw-bubble-btn mlw-bubble-btn--block" onClick={() => pick("blocked")}>
          {tx("Block")}
        </button>
      </div>
    </div>
  );
}

type ToastChoice = "resolve" | "block" | "dismiss";

function WinToast({ onChoose }: { onChoose: (c: ToastChoice) => void }) {
  const [leaving, setLeaving] = useState<ToastChoice | null>(null);
  const onChooseRef = useLatest(onChoose);
  useEffect(() => {
    if (!leaving) return undefined;
    const id = window.setTimeout(() => onChooseRef.current(leaving), 230);
    return () => window.clearTimeout(id);
  }, [leaving, onChooseRef]);
  const pick = (c: ToastChoice) => setLeaving((cur) => cur ?? c);
  return (
    <div className={`mlw-toast${leaving ? " mlw-is-leaving" : ""}`} role="alert">
      <div className="mlw-toast-top">
        <span className="mlw-toast-app" aria-hidden="true">
          <Ic n="globe" size={13} />
        </span>
        <span className="mlw-toast-from">{tx("Chrome · mindspark.example")}</span>
        <button type="button" className="mlw-toast-x" aria-label={tx("Dismiss notification")} onClick={() => pick("dismiss")}>
          <Ic n="x" size={13} />
        </button>
      </div>
      <div className="mlw-toast-main">
        <span className="mlw-toast-ic" aria-hidden="true">
          <Ic n="warn" size={20} />
        </span>
        <div className="mlw-toast-text">
          <strong className="mlw-toast-h">{tx("Security warning: your browser needs attention")}</strong>
          <span className="mlw-toast-p">{tx("Resolve now.")}</span>
        </div>
      </div>
      <div className="mlw-toast-btns">
        <button type="button" className="mlw-toast-btn" onClick={() => pick("resolve")}>
          {tx("Resolve now")}
        </button>
        <button type="button" className="mlw-toast-btn" onClick={() => pick("block")}>
          {tx("Block this site")}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Debrief
 * ---------------------------------------------------------------------------------------------- */

type PillTone = "ok" | "info" | "warn" | "mute";

interface DebriefLine {
  key: string;
  icon: IconName;
  label: string;
  text: string;
  pill: string;
  tone: PillTone;
}

const CONTACT_ONLY = "Allowing notifications does not let a site control your PC. It only lets that site contact you.";
const MALWARE_POINT = "A game site asking you to run a repair or a command is the point where malware would have run.";

function debriefLines(j: Journey): DebriefLine[] {
  const ad: DebriefLine = {
    key: "ad",
    icon: "globe",
    label: "Advertisement",
    text: "The advertisement got you to open the game. Opening it was not the mistake.",
    pill: "Opened the ad",
    tone: "info",
  };

  let notif: DebriefLine;
  if (j.notif === "allowed") {
    notif = {
      key: "notif",
      icon: "bell",
      label: "Notifications",
      text: `You allowed notifications.${j.toastShown > 0 ? " The site used that to send you a warning after you left." : ""}${j.toastBlocked ? " You then blocked the site." : ""} ${CONTACT_ONLY}`,
      pill: "Allowed",
      tone: "info",
    };
  } else if (j.notif === "blocked") {
    notif = {
      key: "notif",
      icon: "bell",
      label: "Notifications",
      text: `You blocked notifications, so the site could not message you later. ${CONTACT_ONLY}`,
      pill: "Blocked",
      tone: "ok",
    };
  } else if (j.notif === "dismissed") {
    notif = {
      key: "notif",
      icon: "bell",
      label: "Notifications",
      text: `You closed the prompt without choosing, so nothing was allowed. ${CONTACT_ONLY}`,
      pill: "Closed the prompt",
      tone: "info",
    };
  } else {
    notif = {
      key: "notif",
      icon: "bell",
      label: "Notifications",
      text: "You left before the site asked to send notifications.",
      pill: "Not asked",
      tone: "mute",
    };
  }

  let ext: DebriefLine;
  if (j.extTried) {
    ext = {
      key: "ext",
      icon: "puzzle",
      label: "Extension",
      text: "You pressed Add extension. The company block stopped it. Looking at the listing did not infect the computer.",
      pill: "Tried to add",
      tone: "warn",
    };
  } else if (j.extViewed) {
    ext = {
      key: "ext",
      icon: "puzzle",
      label: "Extension",
      text: "You opened the Challenge Mode upgrade but did not add it. Looking at the listing did not infect the computer.",
      pill: "Looked only",
      tone: "info",
    };
  } else if (j.reachedOffer && j.sawFreeze) {
    ext = {
      key: "ext",
      icon: "puzzle",
      label: "Extension",
      text: "You skipped the upgrade and kept the basic game.",
      pill: "Skipped",
      tone: "ok",
    };
  } else if (j.reachedOffer) {
    ext = {
      key: "ext",
      icon: "puzzle",
      label: "Extension",
      text: "You closed the page at the offer without opening the upgrade.",
      pill: "Not added",
      tone: "ok",
    };
  } else {
    ext = {
      key: "ext",
      icon: "puzzle",
      label: "Extension",
      text: "You left before the extension was offered.",
      pill: "Not offered",
      tone: "mute",
    };
  }

  let repair: DebriefLine;
  if (j.ranExe && j.ranCmd) {
    repair = {
      key: "repair",
      icon: "shield",
      label: "Repair",
      text: `You tried the download and the command. Both were blocked. ${MALWARE_POINT}`,
      pill: "Both blocked",
      tone: "warn",
    };
  } else if (j.ranExe) {
    repair = {
      key: "repair",
      icon: "shield",
      label: "Repair",
      text: `You tried the download. It was blocked. ${MALWARE_POINT}`,
      pill: "Download blocked",
      tone: "warn",
    };
  } else if (j.sawRepair) {
    repair = {
      key: "repair",
      icon: "shield",
      label: "Repair",
      text: `You closed the page before running anything. ${MALWARE_POINT}`,
      pill: "Closed the tab",
      tone: "ok",
    };
  } else if (j.sawFreeze) {
    repair = {
      key: "repair",
      icon: "shield",
      label: "Repair",
      text: `You closed the tab at the freeze, before any repair was offered. ${MALWARE_POINT}`,
      pill: "Stopped at the freeze",
      tone: "ok",
    };
  } else {
    repair = {
      key: "repair",
      icon: "shield",
      label: "Repair",
      text: `You left before the site could ask for a repair. ${MALWARE_POINT}`,
      pill: "Stopped early",
      tone: "ok",
    };
  }

  return [ad, notif, ext, repair];
}

function Debrief({ j, onBack, onReplay }: { j: Journey; onBack: () => void; onReplay: () => void }) {
  const lines = debriefLines(j);
  const stoppedEarly = j.leftFrom !== null && !j.sawRepair;
  return (
    <section className="mlw-debrief" aria-label={tx("What just happened")}>
      <div className="mlw-debrief-card">
        <header className="mlw-debrief-head">
          <div>
            <span className="mlw-debrief-kick">{tx("Your journey")}</span>
            <h2 className="mlw-debrief-h">{tx("What just happened")}</h2>
          </div>
          {stoppedEarly ? (
            <span className="mlw-chip mlw-chip--ok">
              <Ic n="check" size={12} /> {tx("Stopped early")}
            </span>
          ) : null}
        </header>
        <ol className="mlw-lines">
          {lines.map((l, n) => (
            <li key={l.key} className="mlw-line" style={{ animationDelay: `${120 + n * 110}ms` }}>
              <span className="mlw-line-ic" aria-hidden="true">
                <Ic n={l.icon} size={17} />
              </span>
              <div className="mlw-line-main">
                <div className="mlw-line-head">
                  <span className="mlw-line-k">{l.label}</span>
                  <span className={`mlw-pill mlw-pill--${l.tone}`}>{l.pill}</span>
                </div>
                <p className="mlw-line-t">{l.text}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="mlw-report">
          <Ic n="info" size={16} />
          <span>{tx("Report it to the company helpdesk even if a control already stopped it.")}</span>
          {j.toldIT ? <span className="mlw-chip">{tx("You told IT")}</span> : null}
        </p>
        <div className="mlw-debrief-actions">
          <button type="button" className="mlw-btn mlw-btn--navy mlw-btn--lg mlw-pulse" onClick={onBack}>
            {tx("Back to the terminal")}
          </button>
          {!j.sawRepair ? (
            <button type="button" className="mlw-btn mlw-btn--ghost mlw-btn--lg" onClick={onReplay}>
              {tx("See what the website would have tried next")}
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** An observer replay of the freeze and the repair. Buttons are inert; it ends on its own. */
interface ReplayView {
  freeze: FreezePhase;
  repair: RepairView | null;
  ghost: Ghost | null;
}

const REPLAY_PLAN: ReadonlyArray<readonly [number, Partial<ReplayView>]> = [
  [0, { freeze: "board" }],
  [1900, { freeze: "glitch" }],
  [2650, { freeze: "frozen" }],
  [4200, { ghost: "repair" }],
  [4600, { ghost: null, repair: "download" }],
  [6300, { ghost: "run" }],
  [6700, { ghost: null, repair: "exeBlocked" }],
  [8900, { ghost: "steps" }],
  [9300, { ghost: null, repair: "steps" }],
  [11100, { ghost: "runSteps" }],
  [11500, { ghost: null, repair: "cmdBlocked" }],
];
const REPLAY_END = 14400;

const REPLAY_TABS: TabSpec[] = [
  { id: "news", title: SITE.news.title, loading: false, leaving: false },
  { id: "game", title: SITE.game.title, loading: false, leaving: false },
];

function ReplayScene({ onDone }: { onDone: () => void }) {
  const [v, setV] = useState<ReplayView>({ freeze: "board", repair: null, ghost: null });
  const onDoneRef = useLatest(onDone);
  useEffect(() => {
    const ids = REPLAY_PLAN.map(([at, patch]) => window.setTimeout(() => setV((cur) => ({ ...cur, ...patch })), at));
    const end = window.setTimeout(() => onDoneRef.current(), REPLAY_END);
    return () => {
      ids.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(end);
    };
  }, [onDoneRef]);

  return (
    <div className="mlw-replay">
      <div className="mlw-replay-ribbon">
        <span className="mlw-replay-dot" aria-hidden="true" />
        <span className="mlw-replay-t">{tx("Replay — you already stopped")}</span>
        <button type="button" className="mlw-replay-skip" onClick={() => onDoneRef.current()}>
          {tx("Skip replay")}
        </button>
      </div>
      <div className="mlw-replay-win">
        <ChromeWindow
          tabs={REPLAY_TABS}
          active="game"
          closing={false}
          busy={false}
          bell={false}
          bubble={null}
          locked
          onSelect={noop}
          onCloseTab={noop}
          onCloseWindow={noop}
          onReload={noop}
        >
          <div className="mlw-page mlw-page--game">
            {v.repair === null ? (
              <FreezePage phase={v.freeze} locked pressing={v.ghost === "repair"} onRepair={noop} />
            ) : (
              <RepairPage
                view={v.repair}
                locked
                ghost={v.ghost}
                onRun={noop}
                onShowSteps={noop}
                onRunSteps={noop}
                onTellIT={noop}
                onCloseTab={noop}
              />
            )}
          </div>
        </ChromeWindow>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * The device
 * ---------------------------------------------------------------------------------------------- */

export function MalwareDesk({
  onContinue,
  onGuideChange,
}: {
  onContinue: () => void;
  onGuideChange?: (guide: MalwareGuide | null) => void;
}) {
  const [s, dispatch] = useReducer(reduce, INITIAL);
  const [layerEl, setLayerEl] = useState<HTMLDivElement | null>(null);
  const fit = useStageFit(layerEl);
  const { j } = s;

  // ---- desktop shell -----------------------------------------------------------------------------

  const desk = useMemo<DesktopConfig>(() => {
    const app = (id: string, label: string, icon: string): DesktopApp => ({ id, label, icon, opens: id });
    return {
      clock: "9:16 AM",
      date: "24/06/2026",
      icons: [app("chrome", "Chrome", "chrome"), app("files", "Files", "files"), app("mail", "Mail", "mail")],
      taskbar: [
        { ...app("chrome", "Chrome", "chrome"), id: "tb_chrome" },
        { ...app("files", "Files", "files"), id: "tb_files" },
        { ...app("mail", "Mail", "mail"), id: "tb_mail" },
      ],
    };
  }, []);

  const onDeskOpen = useCallback((appId: string) => {
    if (appId === "chrome") {
      dispatch({ t: "openBrowser" });
      return;
    }
    dispatch({
      t: "nudge",
      msg: appId === "__start" ? "The Start menu is not part of this exercise." : "Only Chrome is part of this exercise.",
    });
  }, []);

  // ---- timers that belong to a stage -------------------------------------------------------------

  useEffect(() => {
    if (!s.loading) return undefined;
    const id = window.setTimeout(() => dispatch({ t: "loaded" }), s.loading === "game" ? 820 : 620);
    return () => window.clearTimeout(id);
  }, [s.loading]);

  useEffect(() => {
    if (!s.starting) return undefined;
    const id = window.setTimeout(() => dispatch({ t: "ask" }), 650);
    return () => window.clearTimeout(id);
  }, [s.starting]);

  const resultShown = s.stage === "play" && s.playDone !== null;
  useEffect(() => {
    if (!resultShown) return undefined;
    const id = window.setTimeout(() => dispatch({ t: "toOffer" }), 3600);
    return () => window.clearTimeout(id);
  }, [resultShown]);

  useEffect(() => {
    if (s.stage !== "freeze") return undefined;
    const a = window.setTimeout(() => dispatch({ t: "freezeTo", phase: "glitch" }), 2300);
    const b = window.setTimeout(() => dispatch({ t: "freezeTo", phase: "frozen" }), 3050);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, [s.stage]);

  useEffect(() => {
    if (!s.closing) return undefined;
    const id = window.setTimeout(() => dispatch({ t: "closeDone" }), 230);
    return () => window.clearTimeout(id);
  }, [s.closing]);

  const wantsToast = j.notif === "allowed" && !j.toastBlocked && j.toastShown === 0 && !j.toldIT;
  useEffect(() => {
    if (s.stage !== "left") return undefined;
    const id = window.setTimeout(() => dispatch({ t: wantsToast ? "toastIn" : "toDebrief" }), wantsToast ? 1000 : 1100);
    return () => window.clearTimeout(id);
  }, [s.stage, wantsToast]);

  useEffect(() => {
    if (!s.nudge) return undefined;
    const id = window.setTimeout(() => dispatch({ t: "nudge", msg: null }), 2300);
    return () => window.clearTimeout(id);
  }, [s.nudge]);

  const onPlayDone = useCallback((correct: number, timedOut: boolean) => dispatch({ t: "playDone", correct, timedOut }), []);
  const onToast = useCallback((c: ToastChoice) => {
    if (c === "resolve") dispatch({ t: "resolve" });
    else if (c === "block") dispatch({ t: "blockSite" });
    else dispatch({ t: "dismissToast" });
  }, []);

  // ---- top-right guide ---------------------------------------------------------------------------

  const guide = buildGuide(s);
  const guideKey = `${guide.step}\u0001${guide.title}\u0001${guide.body}\u0001${guide.actionLabel ?? ""}`;
  const latest = useRef({ guide, onGuideChange });
  useEffect(() => {
    latest.current = { guide, onGuideChange };
  });
  useEffect(() => {
    latest.current.onGuideChange?.(latest.current.guide);
  }, [guideKey]);
  useEffect(
    () => () => {
      latest.current.onGuideChange?.(null);
    },
    [],
  );

  // ---- what the browser shows --------------------------------------------------------------------

  const showBrowser = s.browser.open && s.stage !== "debrief";
  const tabs: TabSpec[] = [];
  if (s.browser.news) tabs.push({ id: "news", title: SITE.news.title, loading: s.loading === "news", leaving: false });
  if (s.browser.game) {
    tabs.push({ id: "game", title: SITE.game.title, loading: s.loading === "game", leaving: s.closing === "tab" });
  }

  const adMode: AdMode = s.stage === "news" && !s.browser.game ? "live" : s.browser.game ? "switch" : "used";

  let gameView: ReactNode = null;
  if (s.skeleton) {
    gameView = <Skeleton />;
  } else if (s.stage === "lobby" || s.stage === "notify") {
    gameView = <LobbyPage starting={s.starting} asking={s.stage === "notify"} onPlay={() => dispatch({ t: "press" })} />;
  } else if (s.stage === "play") {
    gameView = s.playDone ? (
      <ResultCard done={s.playDone} onNext={() => dispatch({ t: "toOffer" })} />
    ) : (
      <PlayRound onDone={onPlayDone} />
    );
  } else if (s.stage === "offer") {
    gameView = (
      <OfferPage
        view={s.offerView}
        onSee={() => dispatch({ t: "seeListing" })}
        onAdd={() => dispatch({ t: "addExt" })}
        onContinue={() => dispatch({ t: "continueBasic" })}
      />
    );
  } else if (s.stage === "freeze") {
    gameView = <FreezePage phase={s.freezePhase} onRepair={() => dispatch({ t: "repair" })} />;
  } else if (s.stage === "repair") {
    gameView = (
      <RepairPage
        view={s.repairView}
        ghost={null}
        onRun={() => dispatch({ t: "run" })}
        onShowSteps={() => dispatch({ t: "showSteps" })}
        onRunSteps={() => dispatch({ t: "runSteps" })}
        onTellIT={() => dispatch({ t: "tellIT" })}
        onCloseTab={() => dispatch({ t: "closeTab", tab: "game" })}
      />
    );
  }

  const bubble =
    s.stage === "notify" && s.browser.active === "game" ? (
      <PermissionBubble onAnswer={(v) => dispatch({ t: "answer", v })} />
    ) : null;

  const stageStyle: CSSProperties = { width: fit.w, height: fit.h, transform: `scale(${fit.s})` };

  return (
    <Desktop
      className="mlw-desk"
      config={desk}
      onOpen={onDeskOpen}
      highlightAppId={s.stage === "desk" ? "chrome" : undefined}
      openAppIds={s.browser.open ? ["chrome"] : []}
      activeAppId={s.browser.open ? "chrome" : null}
    >
      <div className="mlw-layer" ref={setLayerEl} data-stage={s.stage}>
        <div className="mlw-stage" style={stageStyle}>
          {showBrowser ? (
            <ChromeWindow
              tabs={tabs}
              active={s.browser.active}
              closing={s.closing === "window"}
              busy={s.loading === s.browser.active}
              bell={j.notif === "blocked" && s.browser.active === "game"}
              bubble={bubble}
              onSelect={(id) => dispatch({ t: "selectTab", tab: id })}
              onCloseTab={(id) => dispatch({ t: "closeTab", tab: id })}
              onCloseWindow={() => dispatch({ t: "closeWindow" })}
              onReload={() => dispatch({ t: "reload" })}
            >
              {s.browser.news ? (
                <div className={`mlw-page mlw-page--news${s.browser.active === "news" ? "" : " mlw-page--off"}`}>
                  <NewsPage adMode={adMode} onAd={() => dispatch({ t: "openAd" })} />
                </div>
              ) : null}
              {s.browser.game ? (
                <div className={`mlw-page mlw-page--game${s.browser.active === "game" ? "" : " mlw-page--off"}`}>
                  {gameView}
                  {s.stage === "notify" ? <div className="mlw-veil" aria-hidden="true" /> : null}
                </div>
              ) : null}
            </ChromeWindow>
          ) : null}

          {s.stage === "debrief" && !s.replay ? (
            <Debrief j={j} onBack={onContinue} onReplay={() => dispatch({ t: "replay", on: true })} />
          ) : null}
          {s.stage === "debrief" && s.replay ? <ReplayScene onDone={() => dispatch({ t: "replay", on: false })} /> : null}

          {s.toast ? <WinToast onChoose={onToast} /> : null}
          {s.nudge ? (
            <div className="mlw-nudge" role="status">
              {s.nudge}
            </div>
          ) : null}
        </div>
      </div>
    </Desktop>
  );
}
